// Configuración · productos (docs/108 B10): buscar en el servidor, crear y editar la ficha, cambiar su
// estado (con motivo), su múltiplo de venta, habilitarlo en la empresa (vende/compra), agregar
// impuestos, y mantener sus PRESENTACIONES (caja, display…) y CÓDIGOS (EAN, del proveedor).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useCategorias, useDebounce, useMarcas } from '../api/consultas';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla } from '../comun/Grilla';
import { decimal } from '../comun/formato';
import { decimalApi, type CampoForm } from '../comun/Mantenedor';
import { Modal } from '../comun/Modal';
import { useSesion } from '../sesion/SesionContexto';
import { hoyIso } from '../ventas/comercial';
import { NuevoProducto } from './NuevoProducto';

interface ProductoFila {
  id: string;
  sku: string;
  nombre: string;
  nombre_corto: string | null;
  tipo: string;
  estado: string;
  categoria_id: string | null;
  marca_id: string | null;
  unidad_base_codigo: string;
  version: number;
  vendible: boolean | null;
  comprable: boolean | null;
  activo_empresa: boolean | null;
}
interface FichaCompleta {
  producto: Record<string, unknown> & {
    id: string;
    sku: string;
    nombre: string;
    version: number;
    venta_multiplo: string | null;
  };
  presentaciones: Array<Record<string, unknown>>;
  codigos: Array<Record<string, unknown>>;
  impuestos: Array<Record<string, unknown>>;
}

const t = (v: unknown) => (v === null || v === undefined ? '' : String(v));

function useEnviar(claves: unknown[][]) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<
    unknown,
    Error,
    { ruta: string; cuerpo: unknown; metodo?: 'POST' | 'PATCH' | 'PUT' }
  >({
    mutationFn: ({ ruta, cuerpo, metodo = 'POST' }) => {
      const idempotencia = intentos.clave(`prod:${metodo}:${ruta}`, cuerpo);
      return metodo === 'PATCH'
        ? api.patch(ruta, cuerpo, { idempotencia })
        : metodo === 'PUT'
          ? api.put(ruta, cuerpo, { idempotencia })
          : api.post(ruta, cuerpo, { idempotencia });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await Promise.all(claves.map((k) => qc.invalidateQueries({ queryKey: k })));
    },
    onError: (e) => intentos.cerrar(e),
  });
}

/** Formulario simple de varios campos (diálogo). */
function Formulario({
  titulo,
  campos,
  inicial = {},
  conMotivo = false,
  pendiente,
  error,
  alGuardar,
  alCerrar,
}: {
  titulo: string;
  campos: CampoForm[];
  inicial?: Record<string, string>;
  conMotivo?: boolean;
  pendiente: boolean;
  error: unknown;
  alGuardar: (v: Record<string, string>) => void;
  alCerrar: () => void;
}) {
  const [v, setV] = useState<Record<string, string>>(inicial);
  const malos = campos.filter((c) => {
    const x = (v[c.id] ?? '').trim();
    return (c.requerido && !x) || (x && c.patron && !c.patron.test(x));
  });
  const valido = malos.length === 0 && (!conMotivo || (v['motivo'] ?? '').trim().length >= 3);
  const todos = conMotivo
    ? [...campos, { id: 'motivo', etiqueta: 'Motivo', requerido: true } as CampoForm]
    : campos;
  return (
    <Modal titulo={titulo} alCerrar={alCerrar}>
      <form
        aria-label={titulo}
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) alGuardar(v);
        }}
      >
        {todos.map((c) => (
          <label key={c.id} className="campo">
            <span className="etiqueta">
              {c.etiqueta}
              {c.requerido ? ' *' : ''}
            </span>
            {c.tipo === 'seleccion' ? (
              <select
                aria-label={c.etiqueta}
                value={v[c.id] ?? ''}
                onChange={(e) => setV((x) => ({ ...x, [c.id]: e.target.value }))}
              >
                <option value="">—</option>
                {(c.opciones ?? []).map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.texto}
                  </option>
                ))}
              </select>
            ) : c.tipo === 'booleano' ? (
              <input
                type="checkbox"
                aria-label={c.etiqueta}
                checked={(v[c.id] ?? 'false') === 'true'}
                onChange={(e) =>
                  setV((x) => ({ ...x, [c.id]: e.target.checked ? 'true' : 'false' }))
                }
              />
            ) : (
              <input
                aria-label={c.etiqueta}
                value={v[c.id] ?? ''}
                onChange={(e) => setV((x) => ({ ...x, [c.id]: e.target.value }))}
              />
            )}
            {c.ayuda && <span className="apagado">{c.ayuda}</span>}
          </label>
        ))}
        {error ? <PanelError error={error} /> : null}
        <div className="acciones-modal">
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
          <button type="submit" className="primario" disabled={!valido || pendiente}>
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

type Dialogo =
  | { tipo: 'nuevo' }
  | { tipo: 'editar'; p: FichaCompleta['producto'] }
  | { tipo: 'estado' }
  | { tipo: 'multiplo' }
  | { tipo: 'empresa' }
  | { tipo: 'impuesto' }
  | { tipo: 'presentacion' }
  | { tipo: 'codigo' };

export function MaestroProductos({ foco }: { foco?: 'presentaciones' | 'codigos' | undefined }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const categorias = useCategorias();
  const marcas = useMarcas();
  const [texto, setTexto] = useState('');
  const buscado = useDebounce(texto, 300);
  const [sel, setSel] = useState<ProductoFila | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const lista = useQuery({
    queryKey: ['config', 'productos', empresaId, buscado],
    queryFn: () =>
      api.get<ProductoFila[]>('/productos', {
        query: {
          empresaId,
          limite: 200,
          ...(buscado.trim().length >= 2 ? { texto: buscado.trim() } : {}),
        },
      }),
  });
  const ficha = useQuery({
    queryKey: ['config', 'producto', sel?.id],
    queryFn: () => api.get<FichaCompleta>(`/productos/${sel!.id}`),
    enabled: !!sel,
  });
  const enviar = useEnviar([
    ['config', 'productos'],
    ['config', 'producto'],
  ]);
  const qc = useQueryClient();
  // Unidades e impuestos se eligen de listas (escribirlos a mano daba «El servidor falló»).
  const unidades = useQuery({
    queryKey: ['catalogo', 'unidades'],
    queryFn: () => api.get<Array<{ codigo: string; nombre: string }>>('/catalogo/unidades'),
    staleTime: 10 * 60_000,
    enabled: dialogo?.tipo === 'presentacion',
  });
  const impuestos = useQuery({
    queryKey: ['tributario', 'impuestos'],
    queryFn: () =>
      api.get<Array<{ codigo: string; nombre: string; tipo: string; activo: boolean }>>(
        '/tributario/impuestos',
      ),
    staleTime: 10 * 60_000,
    enabled: dialogo?.tipo === 'impuesto',
  });
  const gestiona = puede('catalogo.productos.gestionar');
  const cerrar = () => {
    enviar.reset();
    setDialogo(null);
  };
  const mandar = (ruta: string, cuerpo: unknown, metodo: 'POST' | 'PATCH' | 'PUT' = 'POST') =>
    enviar.mutate({ ruta, cuerpo, metodo }, { onSuccess: () => setDialogo(null) });
  const opcCat = (categorias.data ?? []).map((c) => ({
    valor: c.id,
    texto: `${c.codigo} · ${c.nombre}`,
  }));
  const opcMarca = (marcas.data ?? []).map((m) => ({ valor: m.id, texto: m.nombre }));
  const pid = sel?.id ?? '';
  const f = ficha.data;
  return (
    <div className="pantalla consulta" data-pantalla="maestro-productos">
      {foco && (
        <p className="apagado">
          Las {foco} se mantienen dentro de cada producto: búscalo, elígelo y usa «
          {foco === 'presentaciones' ? 'Nueva presentación' : 'Nuevo código'}».
        </p>
      )}
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar producto"
            placeholder="SKU o nombre…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        {gestiona && (
          <button type="button" className="primario" onClick={() => setDialogo({ tipo: 'nuevo' })}>
            Nuevo producto
          </button>
        )}
      </div>
      {lista.isError && <PanelError error={lista.error} />}
      <Grilla
        etiqueta="Productos"
        columnas={[
          {
            id: 's',
            titulo: 'SKU',
            celda: (p) => <span className="mono">{p.sku}</span>,
            ancho: '9rem',
          },
          { id: 'n', titulo: 'Nombre', celda: (p) => p.nombre },
          { id: 'u', titulo: 'Unidad', celda: (p) => p.unidad_base_codigo, ancho: '5rem' },
          { id: 'e', titulo: 'Estado', celda: (p) => p.estado, ancho: '7rem' },
          { id: 'v', titulo: 'Vende', celda: (p) => (p.vendible ? 'sí' : 'no'), ancho: '5rem' },
          { id: 'c', titulo: 'Compra', celda: (p) => (p.comprable ? 'sí' : 'no'), ancho: '5rem' },
        ]}
        filas={lista.data ?? []}
        clave={(p) => p.id}
        cargando={lista.isLoading}
        vacio="Sin productos"
        alSeleccionar={setSel}
      />
      {sel && f && (
        <section aria-label={`Ficha de ${f.producto.sku}`}>
          <h3 className="titulo-seccion">
            {f.producto.sku} · {f.producto.nombre}{' '}
            <span className="apagado">
              múltiplo de venta:{' '}
              {f.producto.venta_multiplo ? decimal(f.producto.venta_multiplo) : 'libre'}
            </span>
          </h3>
          <div className="acciones-od">
            {gestiona && (
              <button type="button" onClick={() => setDialogo({ tipo: 'editar', p: f.producto })}>
                Editar ficha
              </button>
            )}
            {puede('catalogo.productos.estado') && (
              <button type="button" onClick={() => setDialogo({ tipo: 'estado' })}>
                Cambiar estado
              </button>
            )}
            {puede('catalogo.productos.venta_multiplo') && (
              <button type="button" onClick={() => setDialogo({ tipo: 'multiplo' })}>
                Múltiplo de venta
              </button>
            )}
            {gestiona && (
              <>
                <button type="button" onClick={() => setDialogo({ tipo: 'empresa' })}>
                  Habilitación en la empresa
                </button>
                <button type="button" onClick={() => setDialogo({ tipo: 'impuesto' })}>
                  Agregar impuesto
                </button>
                <button type="button" onClick={() => setDialogo({ tipo: 'presentacion' })}>
                  Nueva presentación
                </button>
              </>
            )}
            {puede('catalogo.codigos.gestionar') && (
              <button type="button" onClick={() => setDialogo({ tipo: 'codigo' })}>
                Nuevo código
              </button>
            )}
          </div>
          <div className="cuerpo-od">
            <table className="grilla compacta" aria-label="Presentaciones">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th className="der">Factor</th>
                  <th>Vende</th>
                  <th>Compra</th>
                </tr>
              </thead>
              <tbody>
                {f.presentaciones.map((p) => (
                  <tr key={t(p['id'])}>
                    <td className="mono">{t(p['codigo'])}</td>
                    <td>{t(p['nombre'])}</td>
                    <td className="der">{decimal(t(p['factor_a_base']))}</td>
                    <td>{p['vendible'] ? 'sí' : 'no'}</td>
                    <td>{p['comprable'] ? 'sí' : 'no'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="grilla compacta" aria-label="Códigos">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Valor</th>
                  <th>Principal</th>
                </tr>
              </thead>
              <tbody>
                {f.codigos.map((c) => (
                  <tr key={t(c['id'])}>
                    <td>{t(c['tipo'])}</td>
                    <td className="mono">{t(c['valor'])}</td>
                    <td>{c['principal'] ? 'sí' : ''}</td>
                  </tr>
                ))}
                {!f.codigos.length && (
                  <tr>
                    <td colSpan={3} className="grilla-vacia">
                      Sin códigos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="apagado">
            Impuestos:{' '}
            {f.impuestos.map((i) => t(i['impuesto_codigo'])).join(', ') || 'ninguno adicional'}
          </p>
        </section>
      )}
      {enviar.isError && !dialogo && <PanelError error={enviar.error} />}
      {dialogo?.tipo === 'nuevo' && (
        <NuevoProducto
          empresaId={empresaId}
          alCreado={(p) => {
            setDialogo(null);
            setTexto(p.sku);
            setSel({
              id: p.productoId,
              sku: p.sku,
              nombre: p.nombre,
              nombre_corto: null,
              tipo: 'producto',
              estado: 'activo',
              categoria_id: null,
              marca_id: null,
              unidad_base_codigo: 'UN',
              version: 1,
              vendible: true,
              comprable: true,
              activo_empresa: true,
            });
            void qc.invalidateQueries({ queryKey: ['config', 'productos'] });
          }}
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'editar' && (
        <Formulario
          titulo={`Editar ${dialogo.p.sku}`}
          campos={[
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
            { id: 'nombreCorto', etiqueta: 'Nombre corto' },
            { id: 'categoriaId', etiqueta: 'Categoría', tipo: 'seleccion', opciones: opcCat },
            { id: 'marcaId', etiqueta: 'Marca', tipo: 'seleccion', opciones: opcMarca },
            { id: 'pesoKg', etiqueta: 'Peso (kg)', ayuda: 'hasta 4 decimales' },
            {
              id: 'retornable',
              etiqueta: 'Envase retornable',
              tipo: 'booleano',
              ayuda:
                'No marcar: un envase retornable todavía no se compra ni se vende en el ERP. Una botella de vidrio desechable NO es retornable.',
            },
          ]}
          inicial={{
            nombre: t(dialogo.p['nombre']),
            nombreCorto: t(dialogo.p['nombre_corto']),
            categoriaId: t(dialogo.p['categoria_id']),
            marcaId: t(dialogo.p['marca_id']),
            pesoKg: t(dialogo.p['peso_kg']),
            retornable: dialogo.p['retornable'] ? 'true' : 'false',
          }}
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(
              `/productos/${pid}`,
              {
                empresaId,
                version: dialogo.p.version,
                nombre: v['nombre'],
                nombreCorto: v['nombreCorto']?.trim() || null,
                categoriaId: v['categoriaId'] || null,
                marcaId: v['marcaId'] || null,
                pesoKg: v['pesoKg']?.trim() ? decimalApi(v['pesoKg']) : null,
                retornable: v['retornable'] === 'true',
              },
              'PATCH',
            )
          }
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'estado' && (
        <Formulario
          titulo={`Estado de ${sel?.sku}`}
          campos={[
            {
              id: 'estado',
              etiqueta: 'Estado',
              tipo: 'seleccion',
              requerido: true,
              opciones: ['activo', 'bloqueado', 'descontinuado'].map((x) => ({
                valor: x,
                texto: x,
              })),
            },
          ]}
          conMotivo
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(`/productos/${pid}/estado`, {
              empresaId,
              estado: v['estado'],
              motivo: v['motivo'],
            })
          }
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'multiplo' && (
        <Formulario
          titulo={`Múltiplo de venta de ${sel?.sku}`}
          campos={[
            {
              id: 'multiplo',
              etiqueta: 'Múltiplo (vacío = libre)',
              ayuda: 'Ej.: 12 obliga a vender de a 12 unidades',
            },
          ]}
          conMotivo
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(`/productos/${pid}/venta-multiplo`, {
              empresaId,
              multiplo: v['multiplo']?.trim() ? decimalApi(v['multiplo']) : null,
              motivo: v['motivo'],
            })
          }
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'empresa' && (
        <Formulario
          titulo={`Habilitación de ${sel?.sku} en ${empresa!.razon_social}`}
          campos={[
            { id: 'activo', etiqueta: 'Activo en la empresa', tipo: 'booleano' },
            { id: 'vendible', etiqueta: 'Se vende', tipo: 'booleano' },
            { id: 'comprable', etiqueta: 'Se compra', tipo: 'booleano' },
          ]}
          inicial={{
            activo: sel?.activo_empresa === false ? 'false' : 'true',
            vendible: sel?.vendible === false ? 'false' : 'true',
            comprable: sel?.comprable === false ? 'false' : 'true',
          }}
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(
              `/productos/${pid}/empresa`,
              {
                empresaId,
                activo: v['activo'] === 'true',
                vendible: v['vendible'] === 'true',
                comprable: v['comprable'] === 'true',
              },
              'PUT',
            )
          }
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'impuesto' && (
        <Formulario
          titulo={`Impuesto de ${sel?.sku}`}
          campos={[
            {
              id: 'impuestoCodigo',
              etiqueta: 'Impuesto',
              tipo: 'seleccion',
              requerido: true,
              opciones: (impuestos.data ?? [])
                .filter((i) => i.tipo === 'adicional' && i.activo)
                .map((i) => ({ valor: i.codigo, texto: `${i.nombre} · ${i.codigo}` })),
            },
            {
              id: 'vigenciaDesde',
              etiqueta: 'Rige desde',
              requerido: true,
              patron: /^\d{4}-\d{2}-\d{2}$/,
              ayuda: 'aaaa-mm-dd; si llegó en una compra anterior, la fecha de esa factura',
            },
          ]}
          inicial={{ vigenciaDesde: hoyIso() }}
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(`/productos/${pid}/impuestos`, {
              empresaId,
              impuestoCodigo: v['impuestoCodigo'],
              vigenciaDesde: v['vigenciaDesde'],
              motivo: 'Impuesto del producto',
            })
          }
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'presentacion' && (
        <Formulario
          titulo={`Nueva presentación de ${sel?.sku}`}
          campos={[
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              patron: /^[A-Z0-9_-]{1,16}$/,
              ayuda: 'Ej.: CJ12, DISP6',
            },
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
            {
              id: 'unidadCodigo',
              etiqueta: 'Unidad',
              tipo: 'seleccion',
              requerido: true,
              opciones: (unidades.data ?? []).map((u) => ({
                valor: u.codigo,
                texto: `${u.codigo} · ${u.nombre}`,
              })),
            },
            {
              id: 'factorABase',
              etiqueta: 'Unidades base que contiene',
              requerido: true,
              patron: /^\d{1,6}([.,]\d{1,6})?$/,
              ayuda: 'Ej.: 12 (botellas o latas por caja)',
            },
            { id: 'vendible', etiqueta: 'Se vende', tipo: 'booleano' },
            { id: 'comprable', etiqueta: 'Se compra', tipo: 'booleano' },
          ]}
          inicial={{ vendible: 'true', comprable: 'true' }}
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(`/productos/${pid}/presentaciones`, {
              empresaId,
              codigo: v['codigo'],
              nombre: v['nombre'],
              unidadCodigo: v['unidadCodigo'],
              factorABase: decimalApi(v['factorABase']),
              vendible: v['vendible'] === 'true',
              comprable: v['comprable'] === 'true',
            })
          }
          alCerrar={cerrar}
        />
      )}
      {dialogo?.tipo === 'codigo' && (
        <Formulario
          titulo={`Nuevo código de ${sel?.sku}`}
          campos={[
            {
              id: 'tipo',
              etiqueta: 'Tipo',
              tipo: 'seleccion',
              requerido: true,
              opciones: ['ean13', 'ean8', 'upc', 'interno', 'alternativo', 'qr'].map((x) => ({
                valor: x,
                texto: x,
              })),
            },
            { id: 'valor', etiqueta: 'Valor', requerido: true },
            {
              id: 'presentacionId',
              etiqueta: 'Presentación',
              tipo: 'seleccion',
              opciones: (f?.presentaciones ?? []).map((p) => ({
                valor: t(p['id']),
                texto: `${t(p['codigo'])} · ${t(p['nombre'])}`,
              })),
            },
            { id: 'principal', etiqueta: 'Principal', tipo: 'booleano' },
          ]}
          pendiente={enviar.isPending}
          error={enviar.isError ? enviar.error : null}
          alGuardar={(v) =>
            mandar(`/productos/${pid}/codigos`, {
              empresaId,
              productoId: pid,
              tipo: v['tipo'],
              valor: v['valor']?.trim(),
              ...(v['presentacionId'] ? { presentacionId: v['presentacionId'] } : {}),
              principal: v['principal'] === 'true',
            })
          }
          alCerrar={cerrar}
        />
      )}
    </div>
  );
}
